import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getImageUrl } from 'core/utils/activityUrlGenerator';
import { resetState } from 'store/storeUtils';
import { routeTo, selectNavigationState } from 'store/slice/navigation';
import { Box, Button, Typography } from '@drone-js/core';
import { searchInitialState, setSortAndFilter } from 'component/page/search/store/search';
import { selectCmsFromBootstrap, selectConfigFromBootstrap } from 'store/slice/bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSortAndFilterQueryString } from 'component/page/search/SortAndFilter/sortAndFilterQuery.utils';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesNoResultClearFilterClickEventActivitiesFieldsV1,
    ActivitiesNoResultNewSearchClickEventActivitiesFieldsV1,
    ActivitiesNoSearchResultsPageSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesNoResultClearFilterClickEventActivitiesManifestV1,
    ActivitiesNoResultNewSearchClickEventActivitiesManifestV1,
    ActivitiesNoSearchResultsPageSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import styled from 'styled-components';
import { FilterState } from 'component/common/Filter/Filter.types';
import { mapSelectedFiltersToAnalyticsField } from 'component/common/Filter/filter.utils';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { ErrorPageActionElementValue, getErrorPageCtaTargetUrl } from 'component/page/error/ActivityStandardErrorPage';

interface Props {
    cityName: string;
    isEmptyFromFiltered: boolean;
    selectedFilters: FilterState;
}
const NoSearchResultStyled = styled(Box)`
    margin-top: 20vh;
    margin-bottom: 20vh;
`;
const filterBtnEventIds = ActivitiesNoResultClearFilterClickEventActivitiesManifestV1.ids;
const newSearchBtnEventIds = ActivitiesNoResultNewSearchClickEventActivitiesManifestV1.ids;

const NoResultsErrorPage: React.FC<Props> = React.memo(({ cityName, isEmptyFromFiltered, selectedFilters }) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const locationSearch = useLocation().search;
    const navigation = useNavigate();
    const dispatch = useDispatch();
    const isRedirectGenericErrorComponent = isOn(FeatureName.RedirectGenericErrorComponent);
    const config = useSelector(selectConfigFromBootstrap);
    const navigationState = useSelector(selectNavigationState);
    const isGlobalKeywordSearch = navigationState.isGlobalKeywordSearchPage;

    React.useEffect(() => {
        const filters = mapSelectedFiltersToAnalyticsField(selectedFilters);
        trackElementEvent<ActivitiesNoSearchResultsPageSeenEventActivitiesFieldsV1>(ActivitiesNoSearchResultsPageSeenEventActivitiesManifestV1.ids, { selected_filters: filters });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters]);

    const onButtonClick = () => {
        if (isEmptyFromFiltered && !isGlobalKeywordSearch) {
            const { sort, filter } = searchInitialState;
            const query = getSortAndFilterQueryString(locationSearch, sort, filter, true, true);
            trackElementEvent<ActivitiesNoResultClearFilterClickEventActivitiesFieldsV1>(filterBtnEventIds, {}, { action_element_value: ErrorPageActionElementValue.NoResults });
            dispatch(setSortAndFilter({ sort, filter }));
            navigation(`?${query}`);
        } else {
            trackElementEvent<ActivitiesNoResultNewSearchClickEventActivitiesFieldsV1>(newSearchBtnEventIds, {}, { action_element_value: ErrorPageActionElementValue.NoResults });
            resetState(dispatch);
            dispatch(routeTo({ path: getErrorPageCtaTargetUrl(config, isRedirectGenericErrorComponent) }));
        }
    };
    const noActivities = cms[126832].replace('{City name}', cityName || '').replace('{Count}', '0');
    const primaryMessage = isGlobalKeywordSearch ? cms[368013] : isEmptyFromFiltered ? cms[252530] : noActivities;
    const secondaryMessage = isGlobalKeywordSearch ? cms[368014] : '';
    const buttonText = isEmptyFromFiltered && !isGlobalKeywordSearch ? cms[252532] : cms[130767];

    return (
        <NoSearchResultStyled
            data-element-name="activities-no-search-results-page"
            data-testid="activities-no-search-results-page"
            py="16"
            px="24"
            display="flex"
            flexDirection="column"
            m="auto"
        >
            <Box mb="12" display="flex" justifyContent="center">
                {/* eslint-disable-next-line @agoda/agoda-a11y/no-non-drone-image, @agoda/agoda-a11y/no-hardcoded-content -- @agoda/agoda-a11y/no-non-drone-image[c618b2fd-cd5c-4244-9303-08e9f227a5e9], @agoda/agoda-a11y/no-hardcoded-content[2ecc630a-36a8-44c9-ba19-6c5997daf49b] */}
                <img alt="Error Occurred" src={getImageUrl('error-no-results.svg')} />
            </Box>
            <Typography variant="heading-default" pb="12" data-testid="activities-no-search-result-message" textAlign="center">
                {primaryMessage}
            </Typography>
            {isGlobalKeywordSearch && (
                <Typography variant="body-s" pb="12" data-testid="activities-no-search-result-secondary-message" textAlign="center">
                    {secondaryMessage}
                </Typography>
            )}
            {isEmptyFromFiltered && (
                <Typography variant="body-s" pb="12" data-element-name="activities-no-result-update-filter" textAlign="center">
                    {cms[252531]}
                </Typography>
            )}
            <Box px="32" py="16" display="flex" justifyContent="center" width="100%">
                <Button
                    data-element-name={isEmptyFromFiltered && !isGlobalKeywordSearch ? 'activities-no-result-clear-filter' : 'activities-no-result-new-search'}
                    data-testid={isEmptyFromFiltered && !isGlobalKeywordSearch ? 'activities-no-result-clear-filter' : 'activities-no-result-new-search'}
                    variant="label"
                    mx="12"
                    hierarchy="secondary"
                    width="content-fit"
                    onClick={onButtonClick}
                    size="l"
                    label={buttonText}
                />
            </Box>
        </NoSearchResultStyled>
    );
});
NoResultsErrorPage.displayName = 'NoResultsErrorPage';

export default NoResultsErrorPage;
